import { useEffect, useMemo, useRef, useState } from "react";
import { Compass, AlertCircle, Heart, X, ChevronLeft, ChevronRight, Sparkles, Mail, Clock } from "lucide-react";
import { ApiService } from "../../services/api";
import type { BeyanSeviye, BolumOneri, CoachLetter, CoachTanismaBody, CoachTanismaForm, YolTeklif, Zorlaninca } from "../../types/navigator";
import CoachOnboardingChat from "./CoachOnboardingChat";

// Koçunla tanış — FORM-ÖNCE sihirbaz (sahibin kararı, 23 Eyl 2026). LLM veri toplamaz: bölüm, sevdiği/kaçtığı
// ders, ders beyanı, kişilik seçilerek girilir; sayılar (sıralama merdiveni, sınav tarihi) koddan gelir.
// Koç yalnız sonunda konuşur — birkaç soru yerine tek bir hoş geldin mektubu. Sohbet yolu ikincil (bağlantı altta).
// YKS'de YOL (şema 14.10, 5 Eki): hedef SAYISI sorulmaz — yerini son deneme söyler; koç sırayı ve günde 1–4 saatle
// dönem sonu beklentisini gösterir, öğrenci yalnız süresini seçer. Beyan ve kişilik adımları YKS'de yok.
// LGS'de de YOL (23 Eki): 8. sınıf dersleri okuldaki gibi birlikte ilerler, beklenti LGS gününe (net + yüzdelik dilim).

interface Props {
  onReady: () => void;
}

type Exam = CoachTanismaBody["targetExam"];
type Step = "sinav" | "hedef" | "yol" | "dersler" | "beyan" | "kisilik";
const fmtNet = (n: number | null | undefined) => (n == null ? "—" : n.toLocaleString("tr-TR", { maximumFractionDigits: 2 }));
const saatEtiketi = (dk: number) => (dk % 60 === 0 ? `${dk / 60} saat` : `${dk} dakika`);
const TYT_KISA: Array<[string, string]> = [["TYT Türkçe", "Türkçe"], ["TYT Matematik", "Matematik"], ["TYT Sosyal", "Sosyal"], ["TYT Fen", "Fen"]];
const LGS_KISA: Array<[string, string]> = [["Türkçe", "Türkçe"], ["Matematik", "Matematik"], ["Fen Bilimleri", "Fen"], ["İnkılap Tarihi", "İnkılap"], ["Din Kültürü", "Din"], ["İngilizce", "İngilizce"]];

const fmt = (n: number | null | undefined) => (n == null ? "—" : n.toLocaleString("tr-TR"));
const shortTitle = (t: string) => t.replace(/^(TYT|AYT|LGS|KPSS|DGS|ALES)\s*-?\s*/i, "").replace(/\s*\(.*?\)\s*/g, "").trim();
const fmtDate = (iso: string | null) => (iso ? new Date(`${iso}T00:00:00`).toLocaleDateString("tr-TR", { day: "numeric", month: "long", year: "numeric" }) : "");

const LEVELS: Array<[BeyanSeviye, string]> = [["iyiyim", "iyi"], ["orta", "orta"], ["kotuyum", "kötü"], ["hic", "hiç bakmadım"]];
const OTURUS = [15, 30, 45, 60, 90, 120];
const ZORLANINCA: Array<[Zorlaninca, string]> = [
  ["birakirim", "Bırakırım"],
  ["video", "Video izlerim"],
  ["soru", "Soru çözerim"],
  ["sorarim", "Birine sorarım"],
  ["diger", "Başka yol denerim"],
];
const BANTLAR: Array<["devlet" | "eniyi" | "genis", string, (m: NonNullable<BolumOneri["merdiven"]>) => number | null]> = [
  ["devlet", "Devlet ortası", (m) => m.devletOrta || m.orta || m.enIyi],
  ["eniyi", "En iyisi", (m) => m.enIyi],
  ["genis", "Geniş tutalım (vakıf da olur)", (m) => m.enGenis],
];

const chip = (active: boolean, tone: "blue" | "rose" | "gray" = "blue") =>
  `rounded-full px-3 py-1.5 text-xs font-medium transition-colors ${
    active
      ? tone === "rose" ? "bg-rose-600 text-white" : "bg-blue-600 text-white"
      : "bg-gray-100 text-gray-700 hover:bg-gray-200"
  }`;

export default function CoachOnboarding({ onReady }: Props) {
  const [mode, setMode] = useState<"form" | "chat">("form");
  const [form, setForm] = useState<CoachTanismaForm | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [step, setStep] = useState<Step>("sinav");

  // Cevaplar
  const [exam, setExam] = useState<Exam | null>(null);
  const [targetDate, setTargetDate] = useState<string>("");
  const [bolumQ, setBolumQ] = useState("");
  const [oneriler, setOneriler] = useState<BolumOneri[]>([]);
  const [bolum, setBolum] = useState<BolumOneri | null>(null);
  const [kararsiz, setKararsiz] = useState(false);
  const [bant, setBant] = useState<"devlet" | "eniyi" | "genis" | null>(null);
  const [puanTuru, setPuanTuru] = useState<"SAY" | "EA" | "SÖZ" | null>(null);
  const [yuzdelik, setYuzdelik] = useState<number | null>(null);
  const [sev, setSev] = useState<Set<string>>(new Set());
  const [kac, setKac] = useState<Set<string>>(new Set());
  const [beyan, setBeyan] = useState<Record<string, BeyanSeviye>>({});
  const [oturusDk, setOturusDk] = useState<number | null>(null);
  const [zorlaninca, setZorlaninca] = useState<Zorlaninca | null>(null);
  const [neden, setNeden] = useState("");
  const [dailyMinutes, setDailyMinutes] = useState<number | null>(null);
  const [teklif, setTeklif] = useState<YolTeklif | null>(null);
  const [teklifLoading, setTeklifLoading] = useState(false);
  const [teklifError, setTeklifError] = useState<string | null>(null);
  const [teklifKey, setTeklifKey] = useState<string | null>(null);

  // Gönderim + mektup
  const [submitting, setSubmitting] = useState(false);
  const [letter, setLetter] = useState<CoachLetter | null>(null);
  const [letterStatus, setLetterStatus] = useState<string | null>(null);
  const [measurementWindow, setMeasurementWindow] = useState(true);
  const [resuming, setResuming] = useState(false);
  const searchTimer = useRef<number | null>(null);

  useEffect(() => {
    (async () => {
      try {
        const res = await ApiService.getCoachTanisma();
        setForm(res);
        const guess = (res.profile?.targetExam as Exam | undefined) || res.sinavTahmini;
        if (guess) {
          setExam(guess);
          setStep(guess === "OKUL" || guess === "DIGER" ? "sinav" : "hedef");
        }
        setTargetDate(res.profile?.targetDate || res.varsayilanTarih || "");
        const pt = (res.profile?.puanTuru || res.puanTuruKayit || "").toUpperCase();
        if (["SAY", "EA", "SÖZ"].includes(pt)) setPuanTuru(pt as "SAY" | "EA" | "SÖZ");
      } catch (e: any) {
        setLoadError(e?.message || "Tanışma verisi yüklenemedi");
      }
    })();
  }, []);

  // Bölüm otomatik tamamlama — yazdıkça, 250 ms bekleyerek
  useEffect(() => {
    if (searchTimer.current) window.clearTimeout(searchTimer.current);
    if (bolum || bolumQ.trim().length < 2) {
      setOneriler([]);
      return;
    }
    searchTimer.current = window.setTimeout(async () => {
      try {
        const res = await ApiService.searchCoachBolum(bolumQ.trim());
        setOneriler(res.items);
      } catch {
        setOneriler([]);
      }
    }, 250);
    return () => {
      if (searchTimer.current) window.clearTimeout(searchTimer.current);
    };
  }, [bolumQ, bolum]);

  const lessons = form?.lessons || [];
  const isYks = exam === "YKS";
  const isLgs = exam === "LGS";
  // LGS yolu yalnız 8. sınıf dersleriyle kurulur; 5-7. sınıf öğrencisi eski adımlarla (dersler/beyan/kişilik) tanışır
  const yolAdimi = isYks || (isLgs && lessons.some((l) => /^\s*8\s*\.?\s*s[ıi]n[ıi]f/i.test(l.title || "")));

  // Adım sırası: sınav sorusu yalnız tahmin yoksa ya da okul/diğer; hedef adımı yalnız YKS/LGS
  const steps = useMemo<Step[]>(() => {
    const out: Step[] = [];
    if (!form?.sinavTahmini || exam === "OKUL" || exam === "DIGER") out.push("sinav");
    if (isYks || isLgs) out.push("hedef");
    if (yolAdimi) out.push("yol");
    else out.push("dersler", "beyan", "kisilik");
    return out;
  }, [form?.sinavTahmini, exam, isYks, isLgs, yolAdimi]);

  // YOL teklifi: puan türü bölümden (ya da "henüz karar vermedim" seçiminden) — adım açılınca yüklenir. LGS'de puan türü yok.
  const yolPuanTuru = isLgs ? null : (bolum?.puanTuru && ["SAY", "EA", "SÖZ"].includes(bolum.puanTuru) ? bolum.puanTuru : puanTuru) || null;
  useEffect(() => {
    if (step !== "yol") return;
    // İstenen anahtarla karşılaştır (sunucunun döndürdüğüyle değil): puan türü bilinmeyen bölümde sunucu SAY
    // varsayar; cevapla kıyaslamak teklifi sonsuz döngüde yeniden isterdi.
    const key = `${isLgs ? "LGS" : "YKS"}:${yolPuanTuru || "-"}`;
    if (teklifKey === key) return;
    let cancelled = false;
    (async () => {
      setTeklifLoading(true);
      setTeklifError(null);
      try {
        const res = await ApiService.getCoachYolTeklif(yolPuanTuru, isLgs ? "LGS" : "YKS");
        if (cancelled) return;
        setTeklifKey(key);
        setTeklif(res);
        setDailyMinutes((cur) => cur ?? res.oneri);
      } catch (e: any) {
        if (!cancelled) { setTeklifKey(key); setTeklifError(e?.message || "Yol hesaplanamadı"); }
      } finally {
        if (!cancelled) setTeklifLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [step, yolPuanTuru, teklifKey, isLgs]);
  const stepIndex = Math.max(0, steps.indexOf(step));

  const hedefSiralama = useMemo(() => {
    if (!bolum?.merdiven || !bant) return null;
    return BANTLAR.find(([k]) => k === bant)?.[2](bolum.merdiven) ?? null;
  }, [bolum, bant]);

  const canNext = (): string | null => {
    if (step === "sinav") {
      if (!exam) return "Neye hazırlanıyorsun?";
      if ((exam === "OKUL" || exam === "DIGER") && !targetDate) return "Hedef tarihi seç";
      return null;
    }
    if (step === "hedef") {
      if (isYks) {
        if (bolum && !bant) return "Nereyi hedefleyelim — bir bant seç";
        if (!bolum && !(kararsiz && puanTuru)) return "Ne okumak istediğini yaz ya da 'henüz karar vermedim' de";
      }
      return null;
    }
    if (step === "yol" && !dailyMinutes) return "Günde ne kadar çalışacağını seç";
    if (step === "beyan" && !Object.keys(beyan).length) return "En az bir ders için seç";
    if (step === "kisilik" && !oturusDk && !zorlaninca && !neden.trim()) return "En az birini seç";
    return null;
  };

  const next = () => {
    const err = canNext();
    if (err) {
      setError(err);
      return;
    }
    setError(null);
    const i = steps.indexOf(step);
    if (i < steps.length - 1) setStep(steps[i + 1]);
    else submit();
  };
  const back = () => {
    setError(null);
    const i = steps.indexOf(step);
    if (i > 0) setStep(steps[i - 1]);
  };

  const toggleSev = (id: string) => {
    const s = new Set(sev), k = new Set(kac);
    if (s.has(id)) s.delete(id);
    else { s.add(id); k.delete(id); }
    setSev(s); setKac(k);
  };
  const toggleKac = (id: string) => {
    const s = new Set(sev), k = new Set(kac);
    if (k.has(id)) k.delete(id);
    else { k.add(id); s.delete(id); }
    setSev(s); setKac(k);
  };

  const submit = async () => {
    if (!exam) return;
    setSubmitting(true);
    setError(null);
    try {
      const body: CoachTanismaBody = {
        targetExam: exam,
        targetDate: targetDate || null,
        bolum: isYks && bolum ? bolum.ad : null,
        hedefBant: isYks && bolum ? bant : null,
        puanTuru: isYks ? (bolum ? null : puanTuru) : null,
        hedefYuzdelik: isLgs ? yuzdelik : null,
        sevdigi: [...sev],
        kacindigi: [...kac],
        ...(yolAdimi
          ? { dailyMinutes }
          : { beyan, kisilik: { oturusDk, zorlaninca, neden: neden.trim() || null } }),
      };
      const res = await ApiService.submitCoachTanisma(body);
      setLetter(res.letter);
      setLetterStatus(res.letterStatus);
      setMeasurementWindow(res.measurementWindow !== false);
    } catch (e: any) {
      setError(e?.message || "Tanışma kaydedilemedi");
    } finally {
      setSubmitting(false);
    }
  };

  // Sohbetle tamamlanmış ama planı kurulmamış tanışma: mektubu yazdır ve başla
  const resume = async () => {
    setResuming(true);
    setError(null);
    try {
      await ApiService.createCoachPlan();
      onReady();
    } catch (e: any) {
      setError(e?.message || "Ölçüm kurulamadı");
      setResuming(false);
    }
  };

  if (mode === "chat") return <CoachOnboardingChat onReady={onReady} onSwitchToForm={() => setMode("form")} />;

  if (loadError) {
    return (
      <div className="mx-auto w-full max-w-2xl px-4 py-5 sm:px-6">
        <div className="flex items-center gap-2 rounded-lg border border-rose-200 bg-rose-50 p-3 text-sm text-rose-700"><AlertCircle className="h-4 w-4" /> {loadError}</div>
      </div>
    );
  }
  if (!form) {
    return (
      <div className="grid min-h-[40vh] place-items-center"><div className="h-8 w-8 animate-spin rounded-full border-b-2 border-blue-600" /></div>
    );
  }

  // ── Mektup ekranı: koç ilk kez burada konuşur ──
  if (letterStatus) {
    return (
      <div className="mx-auto flex w-full max-w-2xl flex-col px-4 py-5 sm:px-6">
        <div className="mb-4 flex items-center gap-3">
          <div className="grid h-11 w-11 shrink-0 place-items-center rounded-lg bg-indigo-100 text-indigo-600"><Mail className="h-6 w-6" /></div>
          <div>
            <p className="text-base font-semibold text-gray-900">Koçundan mektup var</p>
            <p className="text-xs text-gray-500">
              {measurementWindow
                ? "Önce hazırbulunuşluğunu ölçeceğiz; programın sonuçlardan kurulacak. Mektup Mektuplar sekmesinde de duracak."
                : yolAdimi
                  ? "Bu haftanın planı hazır; her Pazar gelecek hafta, bu haftanın sonucuna göre kurulur. Mektup Mektuplar sekmesinde de duracak."
                  : "İlk çalışma programın hazır. Mektup Mektuplar sekmesinde de duracak."}
            </p>
          </div>
        </div>
        {letter ? (
          <article className="rounded-lg border border-gray-200 bg-white p-5 shadow-sm">
            <h2 className="mb-3 text-base font-semibold text-gray-900">{letter.subject}</h2>
            <div className="whitespace-pre-line text-sm leading-relaxed text-gray-800">{letter.body}</div>
          </article>
        ) : (
          <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
            {letterStatus === "PENDING"
              ? "Koçun mektubunu yazdı; öğretmenin onayından sonra Mektuplar sekmesine düşecek."
              : `Mektup şu an hazırlanamadı; Mektuplar sekmesinden takip edebilirsin. ${measurementWindow ? "Ölçümün" : "Programın"} hazır.`}
          </div>
        )}
        <button onClick={onReady} className="mt-4 inline-flex items-center justify-center gap-2 rounded-lg bg-blue-600 px-4 py-3 text-sm font-semibold text-white hover:bg-blue-700">
          <Sparkles className="h-4 w-4" /> Bugüne geç
        </button>
      </div>
    );
  }

  if (submitting) {
    return (
      <div className="mx-auto flex w-full max-w-2xl flex-col items-center px-4 py-16 text-center sm:px-6">
        <div className="mb-4 h-10 w-10 animate-spin rounded-full border-b-2 border-indigo-600" />
        <p className="text-base font-semibold text-gray-900">Koçun sana mektup yazıyor…</p>
        <p className="mt-1 max-w-sm text-xs text-gray-500">{yolAdimi ? "Denemeni okuyor, bu haftanın planını kuruyor." : "Söylediklerini okuyor, ölçüm günlerini sıralıyor."} Bir dakika kadar sürebilir; sayfayı kapatma.</p>
      </div>
    );
  }

  // ── Sohbetle tamamlanmış tanışma ──
  if (form.ready && form.profile) {
    return (
      <div className="mx-auto flex w-full max-w-2xl flex-col px-4 py-5 sm:px-6">
        <div className="rounded-lg border border-gray-200 bg-white p-4">
          <p className="text-sm font-semibold text-gray-900">Tanışman tamam</p>
          <p className="mt-1 text-xs text-gray-500">
            {form.profile.tanisma?.bolum?.ad ? `Hedef: ${form.profile.tanisma.bolum.ad}` : form.profile.targetExam}
            {form.profile.hedefSiralama ? ` · ${fmt(form.profile.hedefSiralama)}` : ""}
          </p>
          {error && <p className="mt-2 text-xs text-rose-700">{error}</p>}
          <button onClick={resume} disabled={resuming} className="mt-3 inline-flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-60">
            {resuming ? <div className="h-4 w-4 animate-spin rounded-full border-b-2 border-white" /> : <Sparkles className="h-4 w-4" />} Ölçüme başla
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto flex w-full max-w-2xl flex-col px-4 py-5 sm:px-6">
      <div className="mb-4 flex items-center gap-3">
        <div className="grid h-11 w-11 shrink-0 place-items-center rounded-lg bg-blue-100 text-blue-600"><Compass className="h-6 w-6" /></div>
        <div className="min-w-0">
          <p className="text-base font-semibold text-gray-900">Koçunla tanış</p>
          <p className="text-xs text-gray-500">Birkaç seçim, iki dakika. Sonunda koçun sana mektup yazacak ve ilk çalışma düzenini kuracak.</p>
        </div>
      </div>

      {/* adım göstergesi */}
      <div className="mb-4 flex items-center gap-1.5">
        {steps.map((s, i) => (
          <div key={s} className={`h-1.5 flex-1 rounded-full ${i <= stepIndex ? "bg-blue-600" : "bg-gray-200"}`} />
        ))}
        <span className="ml-2 shrink-0 text-[11px] text-gray-500">{stepIndex + 1}/{steps.length}</span>
      </div>

      <div className="rounded-lg border border-gray-200 bg-white p-4">
        {step === "sinav" && (
          <section>
            <h2 className="text-sm font-semibold text-gray-900">Neye hazırlanıyorsun?</h2>
            <div className="mt-3 flex flex-wrap gap-1.5">
              {([["YKS", "YKS (üniversite)"], ["LGS", "LGS (lise)"], ["OKUL", "Okul sınavı"], ["DIGER", "Başka bir şey"]] as Array<[Exam, string]>).map(([k, label]) => (
                <button key={k} type="button" onClick={() => { setExam(k); setTargetDate(k === "YKS" || k === "LGS" ? (form.varsayilanTarih && form.sinavTahmini === k ? form.varsayilanTarih : "") : ""); }} className={chip(exam === k)}>
                  {label}
                </button>
              ))}
            </div>
            {(exam === "OKUL" || exam === "DIGER") && (
              <label className="mt-4 block text-xs text-gray-600">
                Hedef tarih
                <input type="date" value={targetDate} min={new Date().toLocaleDateString("sv-SE", { timeZone: "Europe/Istanbul" })} onChange={(e) => setTargetDate(e.target.value)} className="mt-1 block w-full rounded-lg border border-gray-200 px-3 py-2 text-sm outline-none focus:border-blue-400" />
              </label>
            )}
          </section>
        )}

        {step === "hedef" && isYks && (
          <section>
            <h2 className="text-sm font-semibold text-gray-900">Ne okumak istiyorsun?</h2>
            <p className="mt-0.5 text-xs text-gray-500">Bölümü yaz, listeden seç. Sayılar ÖSYM listesinden gelir.</p>
            {!bolum && !kararsiz && (
              <div className="relative mt-3">
                <input value={bolumQ} onChange={(e) => setBolumQ(e.target.value)} placeholder="hukuk, tıp, bilgisayar mühendisliği…" autoFocus className="w-full rounded-lg border border-gray-200 px-3 py-2.5 text-sm outline-none focus:border-blue-400" />
                {oneriler.length > 0 && (
                  <ul className="absolute z-10 mt-1 max-h-64 w-full overflow-auto rounded-lg border border-gray-200 bg-white shadow-lg">
                    {oneriler.map((o) => (
                      <li key={o.ad}>
                        <button type="button" onClick={() => { setBolum(o); setBolumQ(o.ad); setOneriler([]); }} className="flex w-full items-center justify-between px-3 py-2 text-left text-sm hover:bg-blue-50">
                          <span className="truncate text-gray-900">{o.ad}</span>
                          <span className="ml-2 shrink-0 text-[11px] text-gray-500">{o.puanTuru} · {o.programN} program</span>
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}
            {bolum && (
              <div className="mt-3 rounded-lg border border-indigo-200 bg-indigo-50 p-3 text-xs text-indigo-900">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-semibold">{bolum.ad}</span>
                  {bolum.puanTuru && <span className="rounded bg-white px-1.5 py-0.5">{bolum.puanTuru}</span>}
                  <button type="button" onClick={() => { setBolum(null); setBant(null); setBolumQ(""); }} className="ml-auto text-indigo-700 hover:underline">değiştir</button>
                </div>
                {bolum.merdiven && (
                  <div className="mt-2 grid grid-cols-3 gap-2 text-center">
                    <div className="rounded bg-white p-2"><div className="text-[10px] text-gray-500">en iyi</div><div className="font-semibold">{fmt(bolum.merdiven.enIyi)}</div></div>
                    <div className="rounded bg-white p-2"><div className="text-[10px] text-gray-500">devlet ortası</div><div className="font-semibold">{fmt(bolum.merdiven.devletOrta || bolum.merdiven.orta)}</div></div>
                    <div className="rounded bg-white p-2"><div className="text-[10px] text-gray-500">en geniş</div><div className="font-semibold">{fmt(bolum.merdiven.enGenis)}</div></div>
                  </div>
                )}
                <p className="mt-3 font-medium">Nereyi hedefleyelim?</p>
                <div className="mt-1.5 flex flex-wrap gap-1.5">
                  {BANTLAR.map(([k, label, pick]) => (
                    <button key={k} type="button" onClick={() => setBant(k)} className={chip(bant === k)}>
                      {label}{bolum.merdiven ? ` · ${fmt(pick(bolum.merdiven))}` : ""}
                    </button>
                  ))}
                </div>
                {hedefSiralama && <p className="mt-2">Hedef sıralama: <b>{fmt(hedefSiralama)}</b></p>}
              </div>
            )}
            {!bolum && (
              <div className="mt-3">
                <button type="button" onClick={() => setKararsiz(!kararsiz)} className={chip(kararsiz, "gray")}>Henüz karar vermedim</button>
                {kararsiz && (
                  <div className="mt-2">
                    <p className="text-xs text-gray-600">Puan türün hangisi? Bölümü ileride konuşuruz.</p>
                    <div className="mt-1.5 flex flex-wrap gap-1.5">
                      {(["SAY", "EA", "SÖZ"] as const).map((p) => (
                        <button key={p} type="button" onClick={() => setPuanTuru(p)} className={chip(puanTuru === p)}>{p}</button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
            {targetDate && <p className="mt-4 text-[11px] text-gray-500">{form.kurumSinavi ? `${form.kurumSinavi.ad} · ${fmtDate(targetDate)} (kurumun takvimi)` : `Sınav tarihi: ${fmtDate(targetDate)}`}</p>}
          </section>
        )}

        {step === "hedef" && isLgs && (
          <section>
            <h2 className="text-sm font-semibold text-gray-900">Hangi yüzdelik dilimi hedefliyorsun?</h2>
            <p className="mt-0.5 text-xs text-gray-500">Emin değilsen boş geç; ilk denemelerden sonra birlikte bakarız.</p>
            <div className="mt-3 flex flex-wrap gap-1.5">
              {form.yuzdelikSecenekleri.map((y) => (
                <button key={y} type="button" onClick={() => setYuzdelik(yuzdelik === y ? null : y)} className={chip(yuzdelik === y)}>%{y}</button>
              ))}
            </div>
            {targetDate && <p className="mt-4 text-[11px] text-gray-500">{form.kurumSinavi ? `${form.kurumSinavi.ad} · ${fmtDate(targetDate)} (kurumun takvimi)` : `Sınav tarihi: ${fmtDate(targetDate)}`}</p>}
          </section>
        )}

        {step === "yol" && yolAdimi && (
          <section>
            <h2 className="text-sm font-semibold text-gray-900">Yolun</h2>
            <p className="mt-0.5 text-xs text-gray-500">{isLgs
              ? "Nereden başladığını denemen söylüyor; dersler okuldaki gibi birlikte ilerler, her derste kitabın sırasıyla. Sen yalnız günde ne kadar çalışacağını seç."
              : "Nereden başladığını denemen söylüyor; sıra sabit, en çok soru getiren konudan başlar. Sen yalnız günde ne kadar çalışacağını seç."}</p>
            {teklifLoading && <div className="grid place-items-center py-8"><div className="h-7 w-7 animate-spin rounded-full border-b-2 border-blue-600" /></div>}
            {teklifError && (
              <div className="mt-3 space-y-2">
                <p className="text-xs text-rose-700">Yol hesaplanamadı ({teklifError}). Yine de günlük süreni seçebilirsin.</p>
                <button type="button" onClick={() => setTeklifKey(null)} className="text-xs font-medium text-blue-700 hover:underline">Tekrar dene</button>
                <div className="flex flex-wrap gap-1.5">
                  {[60, 120, 180, 240].map((m) => (
                    <button key={m} type="button" onClick={() => setDailyMinutes(m)} className={chip(dailyMinutes === m)}>Günde {saatEtiketi(m)}</button>
                  ))}
                </div>
              </div>
            )}
            {teklif && !teklifLoading && (
              <div className="mt-3 space-y-4">
                {/* Deneme: yerin */}
                <div className="rounded-lg border border-gray-200 p-3 text-xs">
                  {teklif.deneme ? (
                    <>
                      <p className="text-gray-500">Son denemen · {teklif.deneme.examName}</p>
                      {isLgs ? (
                        <p className="mt-1 text-sm font-semibold text-gray-900">LGS {fmtNet(teklif.mevcut?.toplamNet)} net{teklif.mevcut ? ` · yaklaşık ${fmtNet(teklif.mevcut.puan)} puan · yüzdelik dilim ${teklif.mevcut.yuzdelikGosterim || "—"}` : ""}</p>
                      ) : (
                        <p className="mt-1 text-sm font-semibold text-gray-900">TYT {fmtNet(teklif.deneme.tyt?.net)} net{teklif.mevcut ? ` · yaklaşık ${fmtNet(teklif.mevcut.puan)} puan · ${teklif.mevcut.siralamaGosterim}` : ""}</p>
                      )}
                      <p className="mt-1 text-gray-600">{(isLgs ? LGS_KISA : TYT_KISA).map(([k, a]) => `${a} ${fmtNet(teklif.deneme?.netler[k] ?? 0)}`).join(" · ")}</p>
                    </>
                  ) : teklif.yarimDeneme ? (
                    <p className="text-amber-800">Son denemen ({teklif.yarimDeneme.examName}) yarım kalmış: {isLgs ? "LGS" : "TYT"} sorularının yüzde {Math.round(((isLgs ? teklif.yarimDeneme.lgs : teklif.yarimDeneme.tyt)?.completion || 0) * 100)}'i cevaplı. Tam bir {isLgs ? "LGS" : "TYT"} denemesi çözersen yerini daha doğru söyleriz. <a href="/practice-exams" className="font-medium underline">Denemeler</a></p>
                  ) : (
                    <p className="text-gray-700">Henüz tamamlanmış bir {isLgs ? "LGS" : "TYT"} denemen yok. İstersen önce bir deneme çöz (<a href="/practice-exams" className="font-medium text-blue-700 underline">Denemeler</a>); ya da şimdi başla, ilk ayın sonunda çözersin.</p>
                  )}
                </div>

                {teklif.bloklar.every((b) => b.eksik) && (
                  <p className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-xs text-amber-900">Sınıfına {isLgs ? "8. sınıf LGS" : "TYT"} dersleri henüz bağlı değil. Seçtiğin süreyle koçun genel bir plan kuracak; dersler bağlanınca yol programına geçebilirsin.</p>
                )}

                {/* Sıra */}
                <div>
                  <p className="text-xs font-medium text-gray-700">{isLgs ? "Dersler · birlikte ilerler" : "Sıra"}</p>
                  <ol className="mt-1.5 space-y-1">
                    {teklif.bloklar.filter((b) => !b.eksik).slice(0, isLgs ? 6 : 5).map((b, i) => (
                      <li key={b.key} className="flex items-center justify-between gap-2 text-xs text-gray-800">
                        <span className="min-w-0 truncate"><span className="mr-1.5 text-gray-400">{i + 1}.</span>{b.ad}</span>
                        <span className="shrink-0 text-gray-500">{b.bolum} bölüm · ~{b.kalanSaat} saat</span>
                      </li>
                    ))}
                  </ol>
                </div>

                {/* Süre seçenekleri */}
                <div>
                  <p className="text-xs font-medium text-gray-700">Günde ne kadar çalışacaksın? <span className="font-normal text-gray-500">({isLgs ? "LGS günü " : ""}{fmtDate(teklif.donemSonu)}'e kadar, haftada 6 gün)</span></p>
                  <div className="mt-2 grid gap-2 sm:grid-cols-2">
                    {teklif.secenekler.map((o) => (
                      <button key={o.dailyMinutes} type="button" onClick={() => setDailyMinutes(o.dailyMinutes)} className={`rounded-lg border p-3 text-left transition-colors ${dailyMinutes === o.dailyMinutes ? "border-blue-500 bg-blue-50" : "border-gray-200 bg-white hover:bg-gray-50"}`}>
                        <div className="flex items-center justify-between gap-2">
                          <span className="inline-flex items-center gap-1.5 text-sm font-semibold text-gray-900"><Clock className="h-4 w-4 text-blue-600" /> Günde {saatEtiketi(o.dailyMinutes)}</span>
                          {o.dailyMinutes === teklif.oneri && <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-[10px] font-semibold text-emerald-800">önerilen başlangıç</span>}
                        </div>
                        {isLgs ? (
                          <>
                            <p className="mt-1 text-xs text-gray-600">{o.yolBitisTarihi && o.yolBitisTarihi <= teklif.donemSonu ? `Kitapların bitişi: ~${fmtDate(o.yolBitisTarihi)}` : `LGS'ye kadar kitapların yüzde ${o.kitapOrani ?? 0} kadarı biter`} · düzenli gidersen ~{fmtNet(o.toplamNet)} net</p>
                            <p className="mt-0.5 text-[11px] text-gray-500">yaklaşık {fmtNet(o.puan)} puan · yüzdelik dilim {o.yuzdelikGosterim || "—"}</p>
                          </>
                        ) : (
                          <>
                            <p className="mt-1 text-xs text-gray-600">~{o.kapsananSoru} TYT sorusunun konusu · düzenli gidersen ~{fmtNet(o.toplamNet)} net</p>
                            <p className="mt-0.5 text-[11px] text-gray-500">{o.bloklar.filter((b) => b.oran >= 1).length} blok biter{o.bloklar.some((b) => b.oran > 0 && b.oran < 1) ? `, ${o.bloklar.find((b) => b.oran > 0 && b.oran < 1)?.ad} yarıda` : ""}</p>
                          </>
                        )}
                      </button>
                    ))}
                  </div>
                  <p className="mt-2 text-[11px] text-gray-500">Az ama düzenli başla. Plan haftalıktır: her Pazar bu haftanın sonucuna göre gelecek hafta kurulur; düzenli gidersen koçun süreyi artırmayı teklif eder. Beklentiler vaat değil, çalışılan konudaki soruların yüzde 70'i doğru varsayımıyla{isLgs ? " (LGS'de 3 yanlış 1 doğruyu götürür)" : ""}.</p>
                </div>
              </div>
            )}
          </section>
        )}

        {step === "dersler" && (
          <section>
            <h2 className="text-sm font-semibold text-gray-900">Hangi dersleri seviyorsun, hangilerinden kaçıyorsun?</h2>
            <p className="mt-0.5 text-xs text-gray-500">İstediğin kadar işaretle; boş da geçebilirsin. Ölçüm sevdiğin dersten başlar.</p>
            <ul className="mt-3 divide-y divide-gray-100">
              {lessons.map((l) => (
                <li key={l.id} className="flex items-center gap-2 py-2">
                  <span className="min-w-0 flex-1 truncate text-sm text-gray-800">{shortTitle(l.title)}</span>
                  <button type="button" onClick={() => toggleSev(l.id)} aria-label="Seviyorum" className={`grid h-8 w-8 place-items-center rounded-full ${sev.has(l.id) ? "bg-blue-600 text-white" : "bg-gray-100 text-gray-500 hover:bg-gray-200"}`}><Heart className="h-4 w-4" /></button>
                  <button type="button" onClick={() => toggleKac(l.id)} aria-label="Kaçıyorum" className={`grid h-8 w-8 place-items-center rounded-full ${kac.has(l.id) ? "bg-rose-600 text-white" : "bg-gray-100 text-gray-500 hover:bg-gray-200"}`}><X className="h-4 w-4" /></button>
                </li>
              ))}
            </ul>
            <p className="mt-2 text-[11px] text-gray-500"><Heart className="inline h-3 w-3" /> seviyorum · <X className="inline h-3 w-3" /> kaçıyorum</p>
          </section>
        )}

        {step === "beyan" && (
          <section>
            <h2 className="text-sm font-semibold text-gray-900">Kendini nerede görüyorsun?</h2>
            <p className="mt-0.5 text-xs text-gray-500">Tahmin yeter; iki haftalık ölçüm gerçeğini gösterir.</p>
            <ul className="mt-3 space-y-2">
              {lessons.map((l) => (
                <li key={l.id} className="flex flex-wrap items-center gap-1.5">
                  <span className="w-full text-sm text-gray-800 sm:w-36 sm:truncate">{shortTitle(l.title)}</span>
                  {LEVELS.map(([k, label]) => (
                    <button key={k} type="button" onClick={() => setBeyan({ ...beyan, [l.id]: k })} className={chip(beyan[l.id] === k)}>{label}</button>
                  ))}
                </li>
              ))}
            </ul>
            {lessons.some((l) => !beyan[l.id]) && (
              <button type="button" onClick={() => setBeyan(Object.fromEntries(lessons.map((l) => [l.id, beyan[l.id] || "orta"])))} className="mt-3 text-xs font-medium text-blue-700 hover:underline">
                Kalanları "orta" say
              </button>
            )}
          </section>
        )}

        {step === "kisilik" && (
          <section className="space-y-4">
            <div>
              <h2 className="text-sm font-semibold text-gray-900">Bir oturuşta kaç dakika dayanırsın?</h2>
              <div className="mt-2 flex flex-wrap gap-1.5">
                {OTURUS.map((dk) => (
                  <button key={dk} type="button" onClick={() => setOturusDk(oturusDk === dk ? null : dk)} className={chip(oturusDk === dk)}>{dk} dk</button>
                ))}
              </div>
            </div>
            <div>
              <h2 className="text-sm font-semibold text-gray-900">Zorlanınca ne yaparsın?</h2>
              <div className="mt-2 flex flex-wrap gap-1.5">
                {ZORLANINCA.map(([k, label]) => (
                  <button key={k} type="button" onClick={() => setZorlaninca(zorlaninca === k ? null : k)} className={chip(zorlaninca === k)}>{label}</button>
                ))}
              </div>
            </div>
            <div>
              <h2 className="text-sm font-semibold text-gray-900">Neden bu hedef? <span className="font-normal text-gray-500">(isteğe bağlı)</span></h2>
              <textarea value={neden} onChange={(e) => setNeden(e.target.value.slice(0, 200))} rows={2} placeholder="Bir cümle yeter" className="mt-2 w-full rounded-lg border border-gray-200 px-3 py-2 text-sm outline-none focus:border-blue-400" />
              <p className="text-right text-[11px] text-gray-400">{neden.length}/200</p>
            </div>
          </section>
        )}
      </div>

      {error && (
        <div className="mt-3 flex items-center gap-2 rounded-lg border border-rose-200 bg-rose-50 p-3 text-sm text-rose-700"><AlertCircle className="h-4 w-4 shrink-0" /> {error}</div>
      )}

      <div className="mt-4 flex items-center gap-2">
        <button type="button" onClick={back} disabled={stepIndex === 0} className="inline-flex items-center gap-1 rounded-lg border border-gray-200 bg-white px-3 py-2.5 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-40">
          <ChevronLeft className="h-4 w-4" /> Geri
        </button>
        <button type="button" onClick={next} className="inline-flex flex-1 items-center justify-center gap-1 rounded-lg bg-blue-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-blue-700">
          {stepIndex === steps.length - 1 ? (<><Mail className="h-4 w-4" /> Bitir, koçum yazsın</>) : (<>Devam <ChevronRight className="h-4 w-4" /></>)}
        </button>
      </div>

      <button type="button" onClick={() => setMode("chat")} className="mt-4 self-center text-xs text-gray-500 hover:text-blue-700 hover:underline">
        Yazarak tanışmayı tercih ediyorum
      </button>
    </div>
  );
}
